import {
  Shield,
  Plus,
  Edit,
  Eye,
  User as UserIcon,
  Mail,
  Lock,
  Phone,
  Building2,
  ChevronRight,
  Check,
  Settings2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { FormError } from '../components/ui/FormError'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import { Spinner, simulateBackendDelay } from '@/app/lib/loader/loader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'

// Hooks
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'

// ─── Types ───────────────────────────────────────────────────────────────────

interface User {
  id: number
  name: string
  email: string
  role: string
  branch: string
  lastAccess: string
  active: boolean
}

interface NewUserForm {
  nombre: string
  apellidos: string
  email: string
  telefono: string
  password: string
  confirmPassword: string
  rol: string
  sucursales: string[]
  activo: boolean
}

interface Permission {
  id: string
  name: string
  category: string
}

interface Role {
  id: string
  name: string
  description: string
  color: string // tailwind bg token e.g. "bg-red-100 text-red-700"
  badgeColor: string // hex for dot indicator
  isSystem: boolean // system roles can't be deleted or renamed
  active: boolean
  permissions: string[]
}

// ─── Static data ─────────────────────────────────────────────────────────────

const allPermissions: Permission[] = [
  { id: 'pos_sell', name: 'Realizar ventas', category: 'Punto de Venta' },
  {
    id: 'pos_discount',
    name: 'Aplicar descuentos',
    category: 'Punto de Venta',
  },
  { id: 'pos_cancel', name: 'Cancelar ventas', category: 'Punto de Venta' },
  { id: 'pos_cashout', name: 'Corte de caja', category: 'Punto de Venta' },
  { id: 'inv_view', name: 'Ver inventario', category: 'Inventario' },
  { id: 'inv_adjust', name: 'Ajustar inventario', category: 'Inventario' },
  { id: 'inv_transfer', name: 'Transferir productos', category: 'Inventario' },
  {
    id: 'inv_alerts',
    name: 'Gestionar alertas de stock',
    category: 'Inventario',
  },
  { id: 'pur_create', name: 'Crear órdenes de compra', category: 'Compras' },
  { id: 'pur_receive', name: 'Recibir mercancía', category: 'Compras' },
  { id: 'pur_approve', name: 'Aprobar órdenes', category: 'Compras' },
  { id: 'rep_view', name: 'Ver reportes', category: 'Reportes' },
  { id: 'rep_export', name: 'Exportar reportes', category: 'Reportes' },
  { id: 'rep_advanced', name: 'Reportes avanzados', category: 'Reportes' },
  { id: 'cli_view', name: 'Ver clientes', category: 'Clientes' },
  { id: 'cli_edit', name: 'Editar clientes', category: 'Clientes' },
  { id: 'cli_loyalty', name: 'Gestionar lealtad', category: 'Clientes' },
  { id: 'reg_view', name: 'Ver módulo regulatorio', category: 'Regulatorio' },
  {
    id: 'reg_controlled',
    name: 'Manejar controlados',
    category: 'Regulatorio',
  },
  {
    id: 'reg_antibiotics',
    name: 'Manejar antibióticos',
    category: 'Regulatorio',
  },
  { id: 'usr_manage', name: 'Gestionar usuarios', category: 'Administración' },
  { id: 'set_config', name: 'Configurar sistema', category: 'Administración' },
  {
    id: 'branch_manage',
    name: 'Gestionar sucursales',
    category: 'Administración',
  },
]

const sucursales = [
  { id: 'todas', name: 'Todas las sucursales', address: 'Acceso global' },
  {
    id: 'centro',
    name: 'Sucursal Centro',
    address: 'Av. Juárez 245, Col. Centro',
  },
  {
    id: 'norte',
    name: 'Sucursal Norte',
    address: 'Blvd. Insurgentes 1800, Col. Norte',
  },
  { id: 'sur', name: 'Sucursal Sur', address: 'Calle Reforma 890, Col. Sur' }
  /*
  {
    id: 'poniente',
    name: 'Sucursal Poniente',
    address: 'Av. Las Américas 560, Col. Poniente',
  },
  {
    id: 'oriente',
    name: 'Sucursal Oriente',
    address: 'Calle 5 de Mayo 320, Col. Oriente',
  },*/
]

const ROLE_COLORS = [
  { label: 'Rojo', badge: 'bg-red-100 text-red-700', dot: '#ef4444' },
  { label: 'Azul', badge: 'bg-blue-100 text-blue-700', dot: '#3b82f6' },
  { label: 'Verde', badge: 'bg-green-100 text-green-700', dot: '#22c55e' },
  { label: 'Morado', badge: 'bg-purple-100 text-purple-700', dot: '#a855f7' },
  { label: 'Naranja', badge: 'bg-orange-100 text-orange-700', dot: '#f97316' },
  { label: 'Cyan', badge: 'bg-cyan-100 text-cyan-700', dot: '#06b6d4' },
  { label: 'Amarillo', badge: 'bg-yellow-100 text-yellow-700', dot: '#eab308' },
  { label: 'Slate', badge: 'bg-slate-100 text-slate-700', dot: '#64748b' },
]

const initialRoles: Role[] = [
  {
    id: 'admin',
    name: 'Administrador',
    description:
      'Acceso completo al sistema, configuración global y gestión de todos los módulos.',
    color: 'bg-red-100 text-red-700',
    badgeColor: '#ef4444',
    isSystem: true,
    active: true,
    permissions: allPermissions.map((p) => p.id),
  },
  {
    id: 'manager',
    name: 'Encargado',
    description:
      'Gestión operativa de sucursal, reportes, compras e inventario.',
    color: 'bg-blue-100 text-blue-700',
    badgeColor: '#3b82f6',
    isSystem: true,
    active: true,
    permissions: [
      'pos_sell',
      'pos_discount',
      'pos_cancel',
      'pos_cashout',
      'inv_view',
      'inv_adjust',
      'inv_transfer',
      'inv_alerts',
      'pur_create',
      'pur_receive',
      'rep_view',
      'rep_export',
      'cli_view',
      'cli_edit',
      'cli_loyalty',
    ],
  },
  {
    id: 'seller',
    name: 'Vendedor',
    description:
      'Operación del punto de venta, consulta de inventario y atención a clientes.',
    color: 'bg-green-100 text-green-700',
    badgeColor: '#22c55e',
    isSystem: true,
    active: true,
    permissions: ['pos_sell', 'inv_view', 'cli_view'],
  },
  {
    id: 'pharmacy',
    name: 'Farmacéutico',
    description:
      'Acceso a módulo regulatorio, recetas controladas y antibióticos.',
    color: 'bg-purple-100 text-purple-700',
    badgeColor: '#a855f7',
    isSystem: true,
    active: true,
    permissions: [
      'pos_sell',
      'inv_view',
      'cli_view',
      'reg_view',
      'reg_controlled',
      'reg_antibiotics',
    ],
  },
]

const initialUsers: User[] = [
  /*

  / ───── Comentado para no mostrar datos estaticos de usuarios ─────────────────────────────────────────────────────────────
  {
    id: 1,
    name: 'Admin Principal',
    email: 'admin@farmacontrol.com',
    role: 'admin',
    branch: 'Todas',
    lastAccess: '2026-05-27 09:45',
    active: true,
  },
  {
    id: 2,
    name: 'María López',
    email: 'maria.lopez@farmacontrol.com',
    role: 'manager',
    branch: 'Sucursal Centro',
    lastAccess: '2026-05-27 14:20',
    active: true,
  },
  {
    id: 3,
    name: 'Carlos Méndez',
    email: 'carlos.mendez@farmacontrol.com',
    role: 'seller',
    branch: 'Sucursal Norte',
    lastAccess: '2026-05-27 11:15',
    active: true,
  },*/
]

const emptyForm: NewUserForm = {
  nombre: '',
  apellidos: '',
  email: '',
  telefono: '',
  password: '',
  confirmPassword: '',
  rol: 'seller',
  sucursales: [],
  activo: true,
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function groupByCategory(perms: Permission[]) {
  return perms.reduce(
    (acc, p) => {
      ; (acc[p.category] ??= []).push(p)
      return acc
    },
    {} as Record<string, Permission[]>,
  )
}

// ─── Esquemas de validación ─────────────────────────────────────────────────
const userSchema = z.object({
  nombre: z.string().min(1, 'El nombre es requerido'),
  apellidos: z.string().min(1, 'Los apellidos son requeridos'),
  email: z.string().email('Debes ingresar un correo válido'),
  telefono: z.string().min(10, 'El teléfono debe tener al menos 10 caracteres'),
  password: z.string().min(1, 'La contraseña debe tener al menos 8 caracteres'),
  confirmarPassword: z.string().min(1, 'La contraseña debe tener al menos 8 caracteres'),
  rol: z.string().min(1, 'El rol es requerido'),
  sucursales: z.array(z.string()).min(1, 'Selecciona al menos una sucursal'),
  activo: z.boolean(),
}).refine((data) => data.password === data.confirmarPassword, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmarPassword'],
});

type UserFormValidate = z.infer<typeof userSchema>;

// ─── Componente ────────────────────────────────────────────────────────────────

export function Users() {

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<UserFormValidate>({
    resolver: zodResolver(userSchema),
    mode: 'onChange',
    defaultValues: {
      nombre: '',
      apellidos: '',
      email: '',
      telefono: '',
      password: '',
      confirmarPassword: '',
      rol: 'seller',
      sucursales: [],
      activo: true,
    },
  });

  const activoValue = watch('activo');
  const sucursalesValue = watch('sucursales');
  const rolValue = watch('rol');



  // Existing user permission modal
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)
  const [selectedRole, setSelectedRole] = useState<string>('seller')

  // New user modal
  const [showNewUserModal, setShowNewUserModal] = useState(false)
  // const [newUserForm, setNewUserForm] = useState<NewUserForm>(emptyForm)
  const [formStep, setFormStep] = useState<1 | 2>(1)
  //const [formErrors, setFormErrors] = useState<  Partial<Record<keyof NewUserForm, string>>>({})

  // Roles manager modal
  const [showRolesModal, setShowRolesModal] = useState(false)
  const [roles, setRoles] = useState<Role[]>(initialRoles)
  const [activeRoleId, setActiveRoleId] = useState<string>('admin')
  const [isCreatingRole, setIsCreatingRole] = useState(false)
  const [editDraft, setEditDraft] = useState<Role | null>(null)
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    new Set(),
  )
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const grouped = groupByCategory(allPermissions)

  // ── New user modal helpers ──────────────────────────────────────────────────

  const toggleSucursal = (id: string) => {
    //leer las sucursales seleccionadas
    const current = sucursalesValue || [];

    //Si selecciona todas
    if (id === 'todas') {
      setValue('sucursales', current.includes('todas') ? [] : ['todas'], { shouldValidate: true });
      return;
    }
    const withoutAll = current.filter((s) => s !== 'todas')
    if (withoutAll.includes(id)) {
      //deseleccionamos
      setValue('sucursales', withoutAll.filter((s) => s !== id), { shouldValidate: true });
    } else {
      //seleccionamos
      setValue('sucursales', [...withoutAll, id], { shouldValidate: true });
    }
  }

  /*const validateStep1 = () => {
    const e: Partial<Record<keyof NewUserForm, string>> = {}
    if (!newUserForm.nombre.trim()) e.nombre = 'El nombre es requerido'
    if (!newUserForm.apellidos.trim())
      e.apellidos = 'Los apellidos son requeridos'
    if (!newUserForm.email.trim() || !/\S+@\S+\.\S+/.test(newUserForm.email))
      e.email = 'Email inválido'
    if (!newUserForm.password || newUserForm.password.length < 8)
      e.password = 'Mínimo 8 caracteres'
    if (newUserForm.password !== newUserForm.confirmPassword)
      e.confirmPassword = 'Las contraseñas no coinciden'
    setFormErrors(e)
    return Object.keys(e).length === 0
  }*/

  /* const validateStep2 = () => {
     const e: Partial<Record<keyof NewUserForm, string>> = {}
     if (newUserForm.sucursales.length === 0)
       e.sucursales = 'Selecciona al menos una sucursal'
     setFormErrors(e)
     return Object.keys(e).length === 0
   }*/


  const openNewUserModal = () => {
    reset();
    //  setNewUserForm(emptyForm)
    setFormStep(1)
    //  setFormErrors({})
    setShowNewUserModal(true)
  }

  // ── Roles modal helpers ─────────────────────────────────────────────────────

  const openRolesModal = () => {
    setActiveRoleId(roles[0]?.id ?? '')
    setEditDraft(null)
    setIsCreatingRole(false)
    setDeleteConfirmId(null)
    setShowRolesModal(true)
  }

  const startEdit = (role: Role) => {
    setEditDraft({ ...role, permissions: [...role.permissions] })
    setIsCreatingRole(false)
  }

  const startCreate = () => {
    const draft: Role = {
      id: `custom_${Date.now()}`,
      name: '',
      description: '',
      color: 'bg-slate-100 text-slate-700',
      badgeColor: '#64748b',
      isSystem: false,
      active: true,
      permissions: [],
    }
    setEditDraft(draft)
    setIsCreatingRole(true)
  }

  const cancelEdit = () => {
    setEditDraft(null)
    setIsCreatingRole(false)
  }

  const saveDraft = () => {
    if (!editDraft || !editDraft.name.trim()) return
    if (isCreatingRole) {
      setRoles((prev) => [...prev, editDraft])
      setActiveRoleId(editDraft.id)
    } else {
      setRoles((prev) =>
        prev.map((r) => (r.id === editDraft.id ? editDraft : r)),
      )
    }
    setEditDraft(null)
    setIsCreatingRole(false)
  }

  const toggleRoleActive = (id: string) => {
    setRoles((prev) =>
      prev.map((r) => (r.id === id ? { ...r, active: !r.active } : r)),
    )
  }

  const deleteRole = (id: string) => {
    setRoles((prev) => prev.filter((r) => r.id !== id))
    setDeleteConfirmId(null)
    if (activeRoleId === id)
      setActiveRoleId(roles.find((r) => r.id !== id)?.id ?? '')
    if (editDraft?.id === id) setEditDraft(null)
  }

  const toggleDraftPermission = (permId: string) => {
    if (!editDraft) return
    setEditDraft((d) => {
      if (!d) return d
      return d.permissions.includes(permId)
        ? { ...d, permissions: d.permissions.filter((p) => p !== permId) }
        : { ...d, permissions: [...d.permissions, permId] }
    })
  }

  const toggleCategoryAll = (category: string) => {
    if (!editDraft) return
    const catPerms = (grouped[category] ?? []).map((p) => p.id)
    const allChecked = catPerms.every((id) =>
      editDraft.permissions.includes(id),
    )
    setEditDraft((d) => {
      if (!d) return d
      if (allChecked) {
        return {
          ...d,
          permissions: d.permissions.filter((p) => !catPerms.includes(p)),
        }
      }
      const merged = Array.from(new Set([...d.permissions, ...catPerms]))
      return { ...d, permissions: merged }
    })
  }

  const toggleCategory = (cat: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev)
      next.has(cat) ? next.delete(cat) : next.add(cat)
      return next
    })
  }

  const activeRole = roles.find((r) => r.id === activeRoleId)
  //const displayRole = editDraft ?? activeRole;

  // ── Table helpers ───────────────────────────────────────────────────────────

  const getRoleBadge = (roleId: string) => {
    const role = roles.find((r) => r.id === roleId)
    return role
      ? { color: role.color, label: role.name }
      : { color: 'bg-slate-100 text-slate-700', label: roleId }
  }

  const openPermissionsModal = (user: User) => {
    setSelectedUser(user)
    setSelectedRole(user.role)
    setShowPermissionsModal(true)
  }

  // ── Manejo de pasos ───────────────────────────────────────────────────────────
  const NextStep = async () => {
    const isStep1Valid = await trigger([
      'nombre', 'apellidos', 'email', 'telefono', 'password', 'confirmarPassword'
    ]);

    if (isStep1Valid) {
      setFormStep(2);
    } else {
      toast.error("Faltan datos", {
        description: "Completa todos los campos requeridos",
      });
    }
  };

  const onSubmitNewUser = async (data: UserFormValidate) => {
    await simulateBackendDelay(2000);
    toast.success("Usuario guardado en la base de datos simulada");
    setShowNewUserModal(false);
    setFormStep(1);
    reset();
  }

  const onErrorNewUser = (errors: any) => {
    toast.error("Faltan datos", {
      description: "Verifica que hayas seleccionado al menos una sucursal.",
    });
  }


  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Usuarios y Permisos
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {initialUsers.length} usuarios activos
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openRolesModal}>
            <Settings2 className="mr-2 h-4 w-4" />
            Administrar Roles
          </Button>
          <Button
            onClick={openNewUserModal}
            className="bg-[#3B7D3B] hover:bg-[#2d6a2d]"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Usuario
          </Button>
        </div>
      </div>

      {/* Role summary cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {roles
          .filter((r) => r.active)
          .map((role) => {
            const count = initialUsers.filter((u) => u.role === role.id).length
            return (
              <div
                key={role.id}
                className="rounded-lg border border-slate-200 bg-white p-5"
              >
                <div className="mb-3 flex items-center gap-3">
                  <div
                    className="rounded-lg p-2.5"
                    style={{ backgroundColor: role.badgeColor + '1a' }}
                  >
                    <Shield
                      className="h-5 w-5"
                      style={{ color: role.badgeColor }}
                    />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                      {role.name}
                    </h3>
                    <p className="text-xl font-semibold text-slate-900">
                      {count}
                    </p>
                  </div>
                </div>
                <p className="line-clamp-2 text-xs text-slate-500">
                  {role.description}
                </p>
              </div>
            )
          })}
      </div>

      {/* Users table */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Usuario
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Rol
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Sucursal
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Último Acceso
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {initialUsers.map((user) => {
              const { color, label } = getRoleBadge(user.role)
              return (
                <tr key={user.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-medium text-slate-900">
                    {user.name}
                  </td>
                  <td className="px-6 py-4 text-slate-700">{user.email}</td>
                  <td className="px-6 py-4">
                    <Badge className={color}>{label}</Badge>
                  </td>
                  <td className="px-6 py-4 text-slate-700">{user.branch}</td>
                  <td className="px-6 py-4 text-slate-700">
                    {user.lastAccess}
                  </td>
                  <td className="px-6 py-4">
                    <Badge
                      className={
                        user.active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }
                    >
                      {user.active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openPermissionsModal(user)}
                        className="rounded p-1 hover:bg-slate-100"
                      >
                        <Eye className="h-4 w-4 text-slate-600" />
                      </button>
                      <button className="rounded p-1 hover:bg-slate-100">
                        <Edit className="h-4 w-4 text-slate-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          MODAL: Administrar Roles
      ══════════════════════════════════════════════════════════════ */}
      <Dialog
        open={showRolesModal}
        onOpenChange={(open) => {
          if (!open) {
            setEditDraft(null)
            setIsCreatingRole(false)
            setDeleteConfirmId(null)
          }
          setShowRolesModal(open)
        }}
      >
        <DialogContent className="flex max-h-[88vh] flex-col overflow-hidden p-0 sm:max-w-4xl">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-5 pb-4">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <Settings2 className="h-5 w-5 text-[#3B7D3B]" />
              Administrar Roles
            </DialogTitle>
            <DialogDescription className="mt-0.5 text-sm text-slate-500">
              Configura los roles del sistema y sus permisos por módulo.
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-h-0 flex-1 overflow-hidden">
            {/* ── Left panel: role list ───────────────────────────────── */}
            <div className="flex w-64 shrink-0 flex-col border-r border-slate-200 bg-slate-50">
              <div className="border-b border-slate-200 p-3">
                <button
                  onClick={startCreate}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-[#3B7D3B] hover:bg-green-50 hover:text-[#3B7D3B]"
                >
                  <Plus className="h-4 w-4" />
                  Nuevo Rol
                </button>
              </div>

              <div className="flex-1 space-y-1 overflow-y-auto p-2">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => {
                      setActiveRoleId(role.id)
                      setEditDraft(null)
                      setIsCreatingRole(false)
                    }}
                    className={`group w-full rounded-lg px-3 py-2.5 text-left transition-colors ${activeRoleId === role.id && !isCreatingRole
                      ? 'border border-slate-200 bg-white shadow-sm'
                      : 'hover:bg-white'
                      }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{
                          backgroundColor: role.badgeColor,
                          opacity: role.active ? 1 : 0.4,
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className={`truncate text-sm font-medium ${role.active ? 'text-slate-900' : 'text-slate-400'}`}
                        >
                          {role.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {role.permissions.length} permiso
                          {role.permissions.length !== 1 ? 's' : ''}
                          {role.isSystem && ' · Sistema'}
                        </p>
                      </div>
                      {!role.active && (
                        <span className="shrink-0 text-xs text-slate-400">
                          Off
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* ── Right panel: role editor / viewer ──────────────────── */}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {editDraft ? (
                /* Edit / Create form */
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-4">
                    <h3 className="font-semibold text-slate-900">
                      {isCreatingRole
                        ? 'Nuevo Rol'
                        : `Editando: ${editDraft.name || 'Sin nombre'}`}
                    </h3>
                    <button
                      onClick={cancelEdit}
                      className="rounded p-1 hover:bg-slate-100"
                    >
                      <X className="h-4 w-4 text-slate-500" />
                    </button>
                  </div>

                  <div className="flex-1 space-y-6 overflow-y-auto p-6">
                    {/* Basic info */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-slate-700">
                            Nombre del rol{' '}
                            <span className="text-red-500">*</span>
                          </label>
                          <Input
                            placeholder="Ej: Supervisor"
                            value={editDraft.name}
                            disabled={editDraft.isSystem}
                            onChange={(e) =>
                              setEditDraft((d) =>
                                d ? { ...d, name: e.target.value } : d,
                              )
                            }
                          />
                          {editDraft.isSystem && (
                            <p className="flex items-center gap-1 text-xs text-slate-400">
                              <AlertCircle className="h-3 w-3" /> Nombre
                              protegido (rol del sistema)
                            </p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-slate-700">
                            Color de insignia
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {ROLE_COLORS.map((c) => (
                              <button
                                key={c.dot}
                                title={c.label}
                                onClick={() =>
                                  setEditDraft((d) =>
                                    d
                                      ? {
                                        ...d,
                                        color: c.badge,
                                        badgeColor: c.dot,
                                      }
                                      : d,
                                  )
                                }
                                className={`h-6 w-6 rounded-full border-2 transition-all ${editDraft.badgeColor === c.dot
                                  ? 'scale-110 border-slate-900'
                                  : 'border-transparent hover:scale-105'
                                  }`}
                                style={{ backgroundColor: c.dot }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-slate-700">
                          Descripción
                        </label>
                        <Input
                          placeholder="Describe brevemente las responsabilidades de este rol"
                          value={editDraft.description}
                          onChange={(e) =>
                            setEditDraft((d) =>
                              d ? { ...d, description: e.target.value } : d,
                            )
                          }
                        />
                      </div>
                    </div>

                    {/* Permissions by category */}
                    <div>
                      <div className="mb-3 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-slate-900">
                          Funciones y permisos
                        </h4>
                        <span className="text-xs text-slate-500">
                          {editDraft.permissions.length} de{' '}
                          {allPermissions.length} seleccionados
                        </span>
                      </div>

                      <div className="space-y-2">
                        {Object.entries(grouped).map(([category, perms]) => {
                          const catIds = perms.map((p) => p.id)
                          const checkedCount = catIds.filter((id) =>
                            editDraft.permissions.includes(id),
                          ).length
                          const allChecked = checkedCount === catIds.length
                          const someChecked = checkedCount > 0 && !allChecked
                          const isCollapsed = collapsedCategories.has(category)

                          return (
                            <div
                              key={category}
                              className="overflow-hidden rounded-lg border border-slate-200"
                            >
                              {/* Category header */}
                              <div className="flex items-center gap-3 bg-slate-50 px-4 py-2.5">
                                <Checkbox
                                  id={`cat-${category}`}
                                  checked={allChecked}
                                  data-state={
                                    someChecked ? 'indeterminate' : undefined
                                  }
                                  onCheckedChange={() =>
                                    toggleCategoryAll(category)
                                  }
                                />
                                <label
                                  htmlFor={`cat-${category}`}
                                  className="flex-1 cursor-pointer text-sm font-medium text-slate-800"
                                >
                                  {category}
                                </label>
                                <span className="mr-1 text-xs text-slate-500">
                                  {checkedCount}/{catIds.length}
                                </span>
                                <button
                                  onClick={() => toggleCategory(category)}
                                  className="rounded p-0.5 text-slate-500 hover:bg-slate-200"
                                >
                                  {isCollapsed ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronUp className="h-4 w-4" />
                                  )}
                                </button>
                              </div>

                              {/* Permission rows */}
                              {!isCollapsed && (
                                <div className="divide-y divide-slate-100">
                                  {perms.map((perm) => {
                                    const checked =
                                      editDraft.permissions.includes(perm.id)
                                    return (
                                      <div
                                        key={perm.id}
                                        onClick={() =>
                                          toggleDraftPermission(perm.id)
                                        }
                                        className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors ${checked
                                          ? 'bg-green-50'
                                          : 'hover:bg-slate-50'
                                          }`}
                                      >
                                        <Checkbox
                                          id={`perm-${perm.id}`}
                                          checked={checked}
                                          onCheckedChange={() =>
                                            toggleDraftPermission(perm.id)
                                          }
                                          className="pointer-events-none"
                                        />
                                        <label
                                          htmlFor={`perm-${perm.id}`}
                                          className={`flex-1 cursor-pointer text-sm ${checked ? 'font-medium text-slate-900' : 'text-slate-600'}`}
                                        >
                                          {perm.name}
                                        </label>
                                        {checked && (
                                          <Check className="h-3.5 w-3.5 shrink-0 text-[#3B7D3B]" />
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Save bar */}
                  <div className="flex shrink-0 gap-3 border-t border-slate-200 bg-white px-6 py-4">
                    <Button
                      variant="outline"
                      onClick={cancelEdit}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={saveDraft}
                      disabled={!editDraft.name.trim()}
                      className="flex-1 bg-[#3B7D3B] hover:bg-[#2d6a2d]"
                    >
                      <Check className="mr-2 h-4 w-4" />
                      {isCreatingRole ? 'Crear Rol' : 'Guardar Cambios'}
                    </Button>
                  </div>
                </div>
              ) : activeRole ? (
                /* Read-only view of selected role */
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-4">
                    <div className="flex items-center gap-3">
                      <span
                        className="h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: activeRole.badgeColor }}
                      />
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {activeRole.name}
                        </h3>
                        <p className="text-xs text-slate-500">
                          {activeRole.permissions.length} permisos asignados
                        </p>
                      </div>
                      <Badge className={activeRole.color}>
                        {activeRole.isSystem ? 'Sistema' : 'Personalizado'}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      {!activeRole.isSystem &&
                        deleteConfirmId === activeRole.id ? (
                        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5">
                          <span className="text-xs font-medium text-red-700">
                            ¿Eliminar rol?
                          </span>
                          <button
                            onClick={() => deleteRole(activeRole.id)}
                            className="text-xs font-semibold text-red-600 hover:text-red-800"
                          >
                            Sí, eliminar
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="text-xs text-slate-500 hover:text-slate-700"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => toggleRoleActive(activeRole.id)}
                            title={
                              activeRole.active
                                ? 'Desactivar rol'
                                : 'Activar rol'
                            }
                            className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                          >
                            {activeRole.active ? (
                              <ToggleRight className="h-5 w-5 text-[#3B7D3B]" />
                            ) : (
                              <ToggleLeft className="h-5 w-5" />
                            )}
                          </button>
                          {!activeRole.isSystem && (
                            <button
                              onClick={() => setDeleteConfirmId(activeRole.id)}
                              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                          <Button
                            onClick={() => startEdit(activeRole)}
                            size="sm"
                            variant="outline"
                          >
                            <Edit className="mr-1.5 h-3.5 w-3.5" />
                            Editar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 space-y-5 overflow-y-auto p-6">
                    <p className="text-sm text-slate-600">
                      {activeRole.description}
                    </p>

                    {/* Permissions by category (read-only) */}
                    <div className="space-y-2">
                      {Object.entries(grouped).map(([category, perms]) => {
                        const active = perms.filter((p) =>
                          activeRole.permissions.includes(p.id),
                        )
                        if (active.length === 0) return null
                        return (
                          <div
                            key={category}
                            className="overflow-hidden rounded-lg border border-slate-200"
                          >
                            <div className="flex items-center justify-between bg-slate-50 px-4 py-2.5">
                              <span className="text-sm font-medium text-slate-800">
                                {category}
                              </span>
                              <span className="text-xs text-slate-500">
                                {active.length}/{perms.length}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2 p-3">
                              {perms.map((perm) => {
                                const has = activeRole.permissions.includes(
                                  perm.id,
                                )
                                return (
                                  <span
                                    key={perm.id}
                                    className={`rounded-full border px-2.5 py-1 text-xs font-medium ${has
                                      ? 'border-green-200 bg-green-50 text-green-800'
                                      : 'border-slate-200 bg-slate-50 text-slate-400 line-through'
                                      }`}
                                  >
                                    {perm.name}
                                  </span>
                                )
                              })}
                            </div>
                          </div>
                        )
                      })}
                      {/* Categories with no permissions */}
                      {Object.entries(grouped).every(([, perms]) =>
                        perms.every(
                          (p) => !activeRole.permissions.includes(p.id),
                        ),
                      ) && (
                          <p className="py-6 text-center text-sm text-slate-400">
                            Sin permisos asignados
                          </p>
                        )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-1 items-center justify-center text-sm text-slate-400">
                  Selecciona un rol para ver sus detalles
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════
          MODAL: Nuevo Usuario (2 steps)
      ══════════════════════════════════════════════════════════════ */}
      <Dialog
        open={showNewUserModal}
        onOpenChange={(open) => {
          setShowNewUserModal(open)
          if (!open) {
            setFormStep(1)
            reset();
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-slate-900">
              Nuevo Usuario
            </DialogTitle>
            <DialogDescription className="sr-only">
              Registrar nuevo usuario en el sistema
            </DialogDescription>
          </DialogHeader>

          <div className="mb-6 flex items-center gap-2">
            <div
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${formStep === 1 ? 'bg-[#3B7D3B] text-white' : 'bg-green-100 text-green-700'}`}
            >
              {formStep > 1 ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <span>1</span>
              )}
              Datos personales
            </div>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <div
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${formStep === 2 ? 'bg-[#3B7D3B] text-white' : 'bg-slate-100 text-slate-500'}`}
            >
              <span>2</span>
              Rol y sucursales
            </div>
          </div>

          {formStep === 1 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                    <UserIcon className="h-3.5 w-3.5" />
                    Nombre(s) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Ej: Brandon"
                    {...register("nombre")}
                    className={errors.nombre ? 'border-red-400' : ''}
                  />
                  <FormError message={errors.nombre?.message} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">
                    Apellidos <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Ej: Navarrete Flores"
                    {...register("apellidos")}
                    className={errors.apellidos ? 'border-red-400' : ''}
                  />
                  <FormError message={errors.apellidos?.message} />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                  <Mail className="h-3.5 w-3.5" />
                  Correo electrónico <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="usuario@farmaciassaludplus.com"
                  {...register("email")}
                  className={errors.email ? 'border-red-400' : ''}
                />
                <FormError message={errors.email?.message} />
              </div>

              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                  <Phone className="h-3.5 w-3.5" />
                  Teléfono
                </label>
                <Input
                  type="tel"
                  placeholder="Ej: 667 123 4567"
                  {...register("telefono")}
                  className={errors.telefono ? 'border-red-400' : ''}
                />
                <FormError message={errors.telefono?.message} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                    <Lock className="h-3.5 w-3.5" />
                    Contraseña <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="password"
                    placeholder="Mínimo 8 caracteres"
                    {...register("password")}
                    className={errors.password ? 'border-red-400' : ''}
                  />
                  <FormError message={errors.password?.message} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">
                    Confirmar contraseña <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="password"
                    placeholder="Repetir contraseña"
                    {...register("confirmarPassword")}
                    className={errors.confirmarPassword ? 'border-red-400' : ''}
                  />
                  <FormError message={errors.confirmarPassword?.message} />
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <Checkbox
                  id="user-active"
                  checked={activoValue}
                  onCheckedChange={(checked) =>
                    setValue('activo', !!checked, { shouldValidate: true })}
                />
                <div>
                  <label
                    htmlFor="user-active"
                    className="cursor-pointer text-sm font-medium text-slate-700"
                  >
                    Usuario activo
                  </label>
                  <p className="text-xs text-slate-500">
                    El usuario podrá iniciar sesión inmediatamente
                  </p>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type='button'
                  onClick={NextStep}
                  className="bg-[#3B7D3B] hover:bg-[#2d6a2d]"
                >
                  Siguiente <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ─── PASO 2 ────────────────────────────────────────────── */}
          {formStep === 2 && (
            <div className="space-y-6">
              <div>
                <label className="mb-3 block text-sm font-medium text-slate-700">
                  Rol del usuario <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {roles
                    .filter((r) => r.active)
                    .map((role) => (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => setValue('rol', role.id, { shouldValidate: true })}

                        className={`rounded-lg border-2 p-4 text-left transition-all ${rolValue === role.id
                          ? 'border-[#3B7D3B] bg-green-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                      >
                        <div className="mb-1 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: role.badgeColor }}
                            />
                            <span
                              className={`text-sm font-semibold ${rolValue === role.id ? 'text-[#3B7D3B]' : 'text-slate-900'
                                }`}
                            >
                              {role.name}
                            </span>
                          </div>
                          {rolValue === role.id && (
                            <div className="flex h-4 w-4 items-center justify-center rounded-full bg-[#3B7D3B]">
                              <Check className="h-2.5 w-2.5 text-white" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs leading-relaxed text-slate-500">
                          {role.description}
                        </p>
                      </button>
                    ))}
                </div>
                {errors.rol && (
                  <p className="mt-2 text-xs text-red-500">{errors.rol.message}</p>
                )}

                {rolValue && (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="mb-1.5 text-xs font-medium text-slate-600">
                      Permisos de {roles.find((r) => r.id === rolValue)?.name}:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {(roles.find((r) => r.id === rolValue)?.permissions ?? []).map((permId) => {
                        const perm = allPermissions.find((p) => p.id === permId);
                        return perm ? (
                          <span
                            key={permId}
                            className="rounded-full border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-600"
                          >
                            {perm.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1 flex items-center gap-1.5 text-sm font-medium text-slate-700">
                  <Building2 className="h-3.5 w-3.5" />
                  Acceso a sucursales <span className="text-red-500">*</span>
                </label>
                <p className="mb-3 text-xs text-slate-500">
                  Selecciona las sucursales a las que tendrá acceso este usuario
                </p>

                <FormError message={errors.sucursales?.message} />
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  {sucursales.map((suc, idx) => {
                    const currentSucursales = sucursalesValue || [];
                    const isChecked = currentSucursales.includes(suc.id);
                    const isDisabledByAll = suc.id !== 'todas' && currentSucursales.includes('todas');

                    return (
                      <div
                        key={suc.id}
                        onClick={() => !isDisabledByAll && toggleSucursal(suc.id)}
                        className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors ${idx !== 0 ? 'border-t border-slate-100' : ''
                          } ${isChecked || isDisabledByAll ? 'bg-green-50' : 'hover:bg-slate-50'}`}
                      >
                        <Checkbox
                          id={`suc-${suc.id}`}
                          checked={isChecked || isDisabledByAll}
                          onCheckedChange={() => !isDisabledByAll && toggleSucursal(suc.id)}
                          className="pointer-events-none"
                        />
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium ${isChecked || isDisabledByAll ? 'text-[#3B7D3B]' : 'text-slate-700'}`}>
                            {suc.name}
                          </p>
                          <p className="truncate text-xs text-slate-500">{suc.address}</p>
                        </div>
                        {(isChecked || isDisabledByAll) && (
                          <Check className="h-4 w-4 shrink-0 text-[#3B7D3B]" />
                        )}
                      </div>
                    );
                  })}
                </div>
                {(sucursalesValue || []).length > 0 && !(sucursalesValue || []).includes('todas') && (
                  <p className="mt-2 text-xs text-slate-500">
                    {(sucursalesValue || []).length} sucursal
                    {(sucursalesValue || []).length !== 1 ? 'es' : ''} seleccionada
                    {(sucursalesValue || []).length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormStep(1)}
                  className="flex-1"
                >
                  Atrás
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmit(onSubmitNewUser, onErrorNewUser)}
                  className="flex-1 bg-[#3B7D3B] hover:bg-[#2d6a2d]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner className="mr-2 h-4 w-4 text-white" />
                      Creando Usuario...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Crear Usuario
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════
          MODAL: Ver permisos de usuario existente
      ══════════════════════════════════════════════════════════════ */}
      <Dialog
        open={showPermissionsModal}
        onOpenChange={setShowPermissionsModal}
      >
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Permisos del Usuario</DialogTitle>
            <DialogDescription className="sr-only">
              Configurar rol y permisos granulares del usuario
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="font-medium text-slate-900">
                  {selectedUser.name}
                </p>
                <p className="text-sm text-slate-600">{selectedUser.email}</p>
                <div className="mt-3">
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Rol:
                  </label>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles
                        .filter((r) => r.active)
                        .map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4">
                {Object.entries(grouped).map(([category, perms]) => {
                  const rolePerms =
                    roles.find((r) => r.id === selectedRole)?.permissions ?? []
                  return (
                    <div key={category}>
                      <h4 className="mb-2 font-medium text-slate-900">
                        {category}
                      </h4>
                      <div className="space-y-2">
                        {perms.map((perm) => {
                          const isChecked = rolePerms.includes(perm.id)
                          return (
                            <div
                              key={perm.id}
                              className="flex items-center gap-3"
                            >
                              <Checkbox
                                id={`view-${perm.id}`}
                                checked={isChecked}
                                disabled={selectedRole === 'admin'}
                              />
                              <label
                                htmlFor={`view-${perm.id}`}
                                className="cursor-pointer text-sm text-slate-700"
                              >
                                {perm.name}
                              </label>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>

              <Button className="w-full bg-[#3B7D3B] hover:bg-[#2d6a2d]">
                Guardar Cambios
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
